import { motion } from "framer-motion";
import { TrainFront, ShoppingBag, Landmark, Trees, MapPin, ExternalLink } from "lucide-react";

const chapterAnim = {
  initial: { opacity: 0, y: 48 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const NEARBY = [
  { icon: TrainFront, text: "Comboio (linha de Sintra) e autocarros — Lisboa em minutos" },
  { icon: Landmark, text: "Palácio Nacional de Queluz e zonas de lazer por perto" },
  { icon: ShoppingBag, text: "Supermercados, comércio e serviços à porta" },
  { icon: Trees, text: "Zona residencial calma, com restaurantes e esplanadas" },
];

export const Chapters = ({ property, images, chapterImages = {} }) => {
  const mapQuery = `${property?.address || "Rua 9 de Abril, Queluz"}, Portugal`;
  return (
    <section id="espaco" data-testid="chapters-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-36">
      <motion.div {...chapterAnim} className="mb-20 max-w-3xl md:mb-28">
        <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-clay">Manifesto</p>
        <h2 className="font-display text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">
          Feito para quem gosta de <em className="text-clay">chegar e ficar</em>.
        </h2>
      </motion.div>

      <div className="space-y-24 md:space-y-36">
        <motion.article {...chapterAnim} className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
          <div>
            <span className="font-display text-7xl text-line sm:text-8xl">01</span>
            <h3 className="mt-2 font-display text-3xl text-ink sm:text-4xl">O Espaço</h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-fog">
              {property?.description}
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden rounded-2xl"
          >
            <img src={chapterImages.espaco || images[0]} alt="Sala do apartamento" className="aspect-[4/3] w-full object-cover" loading="lazy" />
          </motion.div>
        </motion.article>

        <motion.article {...chapterAnim} className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            className="order-last overflow-hidden rounded-2xl md:order-first"
          >
            <img src={chapterImages.experiencia || images[0]} alt="Terraço do apartamento" className="aspect-[4/3] w-full object-cover" loading="lazy" />
          </motion.div>
          <div>
            <span className="font-display text-7xl text-line sm:text-8xl">02</span>
            <h3 className="mt-2 font-display text-3xl text-ink sm:text-4xl">A Experiência</h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-fog">
              Apartamento limpo e cuidado, camas confortáveis e uma cozinha grande onde apetece cozinhar.
              O terraço amplo é o coração da casa — pequenos-almoços ao sol e fins de tarde longos.
              Ideal para famílias, casais, turistas, trabalhadores e estudantes.
            </p>
          </div>
        </motion.article>

        <motion.article {...chapterAnim} className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
          <div>
            <span className="font-display text-7xl text-line sm:text-8xl">03</span>
            <h3 className="mt-2 font-display text-3xl text-ink sm:text-4xl">A Localização</h3>
            <p className="mt-4 flex items-center gap-2 text-base font-medium text-ink" data-testid="location-address">
              <MapPin size={17} className="shrink-0 text-clay" /> {property?.address || "Rua 9 de Abril, Queluz"}
            </p>
            <ul className="mt-6 space-y-4">
              {NEARBY.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-fog">
                  <Icon size={18} className="mt-0.5 shrink-0 text-clay" />
                  {text}
                </li>
              ))}
            </ul>
            <a
              data-testid="location-maps-link"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-clay transition-colors duration-300 hover:text-clay-dark"
            >
              Abrir no Google Maps <ExternalLink size={14} />
            </a>
          </div>
          <div className="overflow-hidden rounded-2xl" data-testid="location-image">
            <img src={chapterImages.localizacao || images[0]} alt="Lisboa, a minutos de Queluz" className="aspect-[4/3] w-full object-cover" loading="lazy" />
          </div>
        </motion.article>
      </div>
    </section>
  );
};
